// src/components/driver/DriverSelect.jsx
import React, { useState, useEffect } from 'react';
import { Car, Users, Settings, Plus, Bell, Calendar, Navigation } from 'lucide-react';
import { useAuthContext } from '../../contexts/AuthContext';
import { useStudentContext } from '../../contexts/StudentContext';
import { getAllDrivers } from '../../services/driverService';
import { VIEWS, PROGRAM_INFO } from '../../utils/constants';
import { styles } from '../../styles/theme';

const DriverSelect = ({ onSelectDriver, onNavigate }) => {
  const { isAdmin, isStaff, logout } = useAuthContext();
  const { getStudentsByDriver } = useStudentContext();
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    getAllDrivers()
      .then(data => setDrivers(data.filter(d => d.status === 'active')))
      .catch(() => setDrivers([]));
  }, []);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.centerContainer}>
          <h1 style={styles.headerTitle}>Stemtree Pickup Management</h1>
          <p style={styles.headerSubtitle}>{PROGRAM_INFO.address} | {PROGRAM_INFO.phone}</p>
        </div>
      </div>
      
      <div style={styles.centerContainer}>
        {/* Title Section */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={styles.driverIcon}>
            <Car style={{ width: '32px', height: '32px', color: 'white' }} />
          </div>
          <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.5rem' }}>
            Driver Portal
          </h2>
          <p style={{ color: '#6b7280' }}>Select your driver profile to begin</p>
        </div>

        {/* Driver Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
          {drivers.map(driver => {
            const allDriverStudents = getStudentsByDriver(driver.id);
            const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
            const todaysStudents = allDriverStudents.filter(student => {
              const pickupDays = student.pickupDays || '';
              if (typeof pickupDays === 'string') {
                return pickupDays.toLowerCase().includes(today.toLowerCase());
              } else if (Array.isArray(pickupDays)) {
                return pickupDays.some(day => day.toLowerCase() === today.toLowerCase());
              }
              return false;
            });
  
  return (
    <div
      key={driver.id}
      style={styles.driverCard}
      onClick={() => onSelectDriver(driver)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
      }}
    >
      <div style={styles.flexBetween}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{
            width: '48px',
            height: '48px',
            background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Car style={{ width: '24px', height: '24px', color: 'white' }} />
          </div>
          <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: '0', color: '#1f2937' }}>
              {driver.name}
            </h3>
            <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0', fontSize: '0.875rem' }}>
              {driver.phone}
            </p>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6' }}>
            {todaysStudents.length}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Students</div>
        </div>
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#10b981' }}>
          <div style={{ width: '8px', height: '8px', backgroundColor: '#10b981', borderRadius: '50%' }}></div>
          <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Active</span>
        </div>
        <Navigation style={{ width: '20px', height: '20px', color: '#3b82f6' }} />
      </div>
    </div>
          );
          })}
        </div>

        {/* Logout Button */}
        <div style={{ marginBottom: '1rem' }}>
          <button
            style={{
              ...styles.button,
              backgroundColor: '#ef4444',
              width: '200px'
            }}
            onClick={logout}
          >
            🚪 Logout
          </button>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          {isAdmin() && (
            <>
              <button
                style={styles.buttonGreen}
                onClick={() => onNavigate(VIEWS.STAFF_DASHBOARD)}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <Settings style={{ width: '18px', height: '18px' }} />
                  <span>Staff Dashboard</span>
                </div>
              </button>
              
              <button
                style={styles.button}
                onClick={() => onNavigate(VIEWS.ALL_STUDENTS)}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <Users style={{ width: '18px', height: '18px' }} />
                  <span>All Students</span>
                </div>
              </button>

              <button
                style={{
                  ...styles.button,
                  backgroundColor: '#6b7280'
                }}
                onClick={() => onNavigate(VIEWS.ARCHIVED_STUDENTS)}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <Users style={{ width: '18px', height: '18px' }} />
                  <span>Archived Students</span>
                </div>
              </button>
              
              <button
                style={styles.button}
                onClick={() => onNavigate(VIEWS.ADD_STUDENT)}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <Plus style={{ width: '18px', height: '18px' }} />
                  <span>Add Student</span>
                </div>
              </button>
              
              <button
                style={styles.buttonOrange}
                onClick={() => onNavigate(VIEWS.JOTFORM_SETUP)}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <Bell style={{ width: '18px', height: '18px' }} />
                  <span>Settings</span>
                </div>
              </button>

              <button
                style={{
                  ...styles.button,
                  backgroundColor: '#8b5cf6'
                }}
                onClick={() => onNavigate(VIEWS.CAMP_ROSTER)}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <Calendar style={{ width: '18px', height: '18px' }} />
                  <span>Camp Roster</span>
                </div>
              </button>
            </>
          )}
          
          {isStaff() && !isAdmin() && (
            <>
              <button
                style={styles.buttonGreen}
                onClick={() => onNavigate(VIEWS.STAFF_DASHBOARD)}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <Settings style={{ width: '18px', height: '18px' }} />
                  <span>Staff Dashboard</span>
                </div>
              </button>
              
              <button
                style={styles.button}
                onClick={() => onNavigate(VIEWS.ALL_STUDENTS)}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <Users style={{ width: '18px', height: '18px' }} />
                  <span>All Students</span>
                </div>
              </button>
              
              <button
                style={styles.button}
                onClick={() => onNavigate(VIEWS.ADD_STUDENT)}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <Plus style={{ width: '18px', height: '18px' }} />
                  <span>Add Student</span>
                </div>
              </button>
            </>
          )}

          {/* ADD THIS - Back to Admin Dashboard Button for Admins */}
{isAdmin() && (
  <button
    style={{ ...styles.button, backgroundColor: '#6b7280' }}
    onClick={() => onNavigate('admin-dashboard')}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
      <Settings style={{ width: '18px', height: '18px' }} />
      <span>Back to Admin Dashboard</span>
    </div>
  </button>
)}
        </div>
      </div>
    </div>
  );
};

export default DriverSelect;